import React, { useState, FormEvent, useRef } from 'react';
import { useLang } from '@shootout-comps/i18n';
import PasswordInput from '../components/PasswordInput';

interface ForgotPasswordPageProps {
  onBack: () => void;       // back to sign-in
  onDone: () => void;       // after password successfully reset
}

type Step = 'email' | 'code' | 'password';

const inp: React.CSSProperties = {
  width: '100%', padding: '0.75rem 1rem',
  background: 'rgba(0,0,0,0.3)', border: '1.5px solid rgba(201,168,76,0.3)',
  borderRadius: '8px', color: 'var(--chalk)', outline: 'none',
  fontSize: '0.95rem', boxSizing: 'border-box',
};
const lbl: React.CSSProperties = {
  fontSize: '0.72rem', color: 'var(--chalk-dim)', textTransform: 'uppercase',
  letterSpacing: '0.1em', display: 'block', marginBottom: '0.35rem',
};

function pwStrength(pw: string): number | null {
  if (!pw) return null;
  return (pw.length >= 12 ? 1 : 0) + (/[A-Z]/.test(pw) ? 1 : 0) +
         (/[0-9]/.test(pw)  ? 1 : 0) + (/[^A-Za-z0-9]/.test(pw) ? 1 : 0);
}

export default function ForgotPasswordPage({ onBack, onDone }: ForgotPasswordPageProps) {
  const { t } = useLang();

  const [step, setStep]               = useState<Step>('email');
  const [email, setEmail]             = useState('');
  const [code, setCode]               = useState('');
  const [sessionToken, setSessionToken] = useState('');
  const [newPw, setNewPw]             = useState('');
  const [confPw, setConfPw]           = useState('');
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');
  const [done, setDone]               = useState(false);

  // Refs for the 6 digit code boxes — must be declared at top level (Rules of Hooks)
  const ref0 = useRef<HTMLInputElement>(null);
  const ref1 = useRef<HTMLInputElement>(null);
  const ref2 = useRef<HTMLInputElement>(null);
  const ref3 = useRef<HTMLInputElement>(null);
  const ref4 = useRef<HTMLInputElement>(null);
  const ref5 = useRef<HTMLInputElement>(null);
  const codeRefs = [ref0, ref1, ref2, ref3, ref4, ref5];

  const goldBtn: React.CSSProperties = {
    width: '100%', marginTop: '0.5rem',
    background: 'linear-gradient(135deg, var(--gold), var(--gold-light))',
    color: 'var(--baize-dark)', border: 'none', borderRadius: '8px',
    padding: '0.9rem', fontSize: '1rem', fontWeight: 700,
    letterSpacing: '0.06em', cursor: 'pointer', minHeight: '48px',
  };

  // ── Step 1: request code ──────────────────────────────────────────────────
  const doRequestCode = async (emailAddr: string) => {
    setLoading(true); setError('');
    try {
      const res = await fetch('/api/auth/request-reset-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailAddr.trim() }),
      });
      const data = await res.json() as { message?: string; error?: string };
      if (!res.ok) { setError(data.error ?? 'Failed'); return; }
      setStep('code');
    } catch { setError('Network error. Please try again.'); }
    finally { setLoading(false); }
  };

  const handleRequestCode = (e: FormEvent) => {
    e.preventDefault();
    doRequestCode(email);
  };

  // ── Step 2: verify code ───────────────────────────────────────────────────
  const handleVerifyCode = async (e: FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) { setError('Please enter the full 6-digit code.'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch('/api/auth/verify-reset-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), code }),
      });
      const data = await res.json() as { sessionToken?: string; error?: string };
      if (!res.ok) { setError(data.error ?? 'Invalid code'); return; }
      setSessionToken(data.sessionToken ?? '');
      setStep('password');
    } catch { setError('Network error. Please try again.'); }
    finally { setLoading(false); }
  };

  // ── Step 3: set new password ──────────────────────────────────────────────
  const handleSetPassword = async (e: FormEvent) => {
    e.preventDefault();
    if (newPw !== confPw) { setError('Passwords do not match.'); return; }
    if (newPw.length < 8) { setError(t.passwordMinLength); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch('/api/auth/reset-password-with-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionToken, password: newPw }),
      });
      const data = await res.json() as { message?: string; error?: string };
      if (!res.ok) { setError(data.error ?? 'Failed'); return; }
      setDone(true);
    } catch { setError('Network error. Please try again.'); }
    finally { setLoading(false); }
  };

  // Digit input box handler
  const handleDigitInput = (i: number, val: string) => {
    const digit = val.replace(/\D/g, '').slice(-1);
    const arr = code.split('');
    arr[i] = digit;
    const next = arr.join('').padEnd(6, '').slice(0, 6);
    setCode(next.trimEnd()); // trim trailing empty
    setCode(arr.slice(0, 6).join(''));
    if (digit && i < 5) codeRefs[i + 1].current?.focus();
  };

  const handleDigitKeyDown = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      const arr = code.split('');
      if (arr[i]) {
        arr[i] = '';
        setCode(arr.join(''));
      } else if (i > 0) {
        codeRefs[i - 1].current?.focus();
      }
    } else if (e.key === 'ArrowLeft' && i > 0) {
      codeRefs[i - 1].current?.focus();
    } else if (e.key === 'ArrowRight' && i < 5) {
      codeRefs[i + 1].current?.focus();
    }
  };

  const handleDigitPaste = (e: React.ClipboardEvent) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setCode(pasted);
      codeRefs[5].current?.focus();
      e.preventDefault();
    }
  };

  const strScore = pwStrength(newPw);
  const str = strScore === null ? null
    : strScore <= 1 ? { label: t.pwStrengthWeak,   color: '#e74c3c', w: '25%'  }
    : strScore === 2 ? { label: t.pwStrengthFair,   color: '#e67e22', w: '50%'  }
    : strScore === 3 ? { label: t.pwStrengthGood,   color: '#f1c40f', w: '75%'  }
    :                  { label: t.pwStrengthStrong, color: '#2ecc71', w: '100%' };

  const card: React.CSSProperties = {
    background: 'linear-gradient(135deg, var(--baize) 0%, var(--felt) 100%)',
    border: '2px solid rgba(201,168,76,0.4)', borderRadius: '16px',
    padding: 'clamp(1.5rem, 6vw, 3rem)', width: '100%', maxWidth: '400px',
    boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
  };

  return (
    <div style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'var(--baize-dark)', padding: '1rem' }}>
      <div style={card}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '1.75rem' }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.4rem' }}>🔑</div>
          <h1 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(1.5rem, 5vw, 2rem)', color: 'var(--gold)', lineHeight: 1, margin: 0 }}>
            {done ? t.passwordUpdated : t.resetPassword}
          </h1>
          {/* Step indicators */}
          {!done && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '0.4rem', marginTop: '0.85rem' }}>
              {(['email', 'code', 'password'] as Step[]).map((s, i) => (
                <div key={s} style={{ width: '28px', height: '4px', borderRadius: '2px', background: step === s ? 'var(--gold)' : i < (['email','code','password'] as Step[]).indexOf(step) ? 'rgba(201,168,76,0.4)' : 'rgba(255,255,255,0.12)', transition: 'background 0.3s' }} />
              ))}
            </div>
          )}
        </div>

        {/* ── Done ── */}
        {done && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>✅</div>
            <p style={{ color: '#2ecc71', marginBottom: '1.5rem', lineHeight: 1.6, fontSize: '0.9rem' }}>
              Your password has been updated. You can now sign in.
            </p>
            <button onClick={onDone} style={goldBtn}>{t.signInNow}</button>
          </div>
        )}

        {/* ── Step 1: Email ── */}
        {!done && step === 'email' && (
          <form onSubmit={handleRequestCode} style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
            <p style={{ color: 'var(--chalk-dim)', fontSize: '0.82rem', margin: 0, lineHeight: 1.6 }}>
              Enter your email address and we'll send you a 6-digit code to reset your password.
            </p>
            <div>
              <label style={lbl}>{t.emailAddress}</label>
              <input
                type="email" value={email} onChange={e => setEmail(e.target.value)}
                autoComplete="email" required autoFocus
                style={inp}
                placeholder="your@email.com"
              />
            </div>
            {error && <div style={{ background: 'rgba(192,57,43,0.2)', border: '1px solid rgba(192,57,43,0.4)', borderRadius: '6px', padding: '0.6rem 0.85rem', color: '#e74c3c', fontSize: '0.82rem' }}>{error}</div>}
            <button type="submit" disabled={loading || !email} style={{ ...goldBtn, opacity: loading || !email ? 0.55 : 1 }}>
              {loading ? t.resending : t.sendResetCode}
            </button>
            <button type="button" onClick={onBack} style={{ width: '100%', background: 'transparent', color: 'var(--chalk-dim)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', padding: '0.7rem', fontSize: '0.88rem', cursor: 'pointer', minHeight: '44px' }}>
              {t.backToSignIn}
            </button>
          </form>
        )}

        {/* ── Step 2: Code ── */}
        {!done && step === 'code' && (
          <form onSubmit={handleVerifyCode} style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
            <p style={{ color: 'var(--chalk-dim)', fontSize: '0.82rem', margin: 0, lineHeight: 1.6 }}>
              We sent a 6-digit code to <strong style={{ color: 'var(--chalk)' }}>{email}</strong>. Enter it below — it expires in 15 minutes.
            </p>

            {/* 6 digit boxes */}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', margin: '0.25rem 0' }}>
              {Array.from({ length: 6 }, (_, i) => (
                <input
                  key={i}
                  ref={codeRefs[i]}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={code[i] ?? ''}
                  onChange={e => handleDigitInput(i, e.target.value)}
                  onKeyDown={e => handleDigitKeyDown(i, e)}
                  onPaste={handleDigitPaste}
                  autoFocus={i === 0}
                  style={{
                    width: '48px', height: '56px', textAlign: 'center',
                    fontSize: '1.5rem', fontWeight: 700, fontFamily: 'Bebas Neue, monospace',
                    background: 'rgba(0,0,0,0.3)',
                    border: `2px solid ${code[i] ? 'var(--gold)' : 'rgba(201,168,76,0.3)'}`,
                    borderRadius: '8px', color: 'var(--gold)', outline: 'none',
                    caretColor: 'transparent',
                  }}
                />
              ))}
            </div>

            {error && <div style={{ background: 'rgba(192,57,43,0.2)', border: '1px solid rgba(192,57,43,0.4)', borderRadius: '6px', padding: '0.6rem 0.85rem', color: '#e74c3c', fontSize: '0.82rem', textAlign: 'center' }}>{error}</div>}
            <button type="submit" disabled={loading || code.length < 6} style={{ ...goldBtn, opacity: loading || code.length < 6 ? 0.55 : 1 }}>
              {loading ? t.verifying : t.verifyCode}
            </button>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="button" onClick={() => { setStep('email'); setCode(''); setError(''); }} style={{ flex: 1, background: 'transparent', color: 'var(--chalk-dim)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', padding: '0.7rem', fontSize: '0.82rem', cursor: 'pointer', minHeight: '40px' }}>
                ← Back
              </button>
              <button type="button" onClick={() => { setCode(''); setError(''); doRequestCode(email); }} style={{ flex: 1, background: 'transparent', color: 'var(--chalk-dim)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', padding: '0.7rem', fontSize: '0.82rem', cursor: 'pointer', minHeight: '40px' }}>
                {t.resendCode}
              </button>
            </div>
          </form>
        )}

        {/* ── Step 3: New password ── */}
        {!done && step === 'password' && (
          <form onSubmit={handleSetPassword} style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
            <p style={{ color: 'var(--chalk-dim)', fontSize: '0.82rem', margin: 0, lineHeight: 1.6 }}>
              Code verified ✓ — now choose a new password for your account.
            </p>
            <div>
              <label style={lbl}>{t.newPassword}</label>
              <PasswordInput
                value={newPw} onChange={e => setNewPw(e.target.value)}
                autoComplete="new-password" required autoFocus style={inp}
              />
              {str && (
                <>
                  <div style={{ height: '3px', background: 'rgba(255,255,255,0.08)', borderRadius: '2px', marginTop: '6px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: str.w, background: str.color, transition: 'all 0.3s' }} />
                  </div>
                  <p style={{ fontSize: '0.68rem', color: str.color, marginTop: '3px' }}>{str.label}</p>
                </>
              )}
            </div>
            <div>
              <label style={lbl}>{t.confirmNewPassword}</label>
              <PasswordInput
                value={confPw} onChange={e => setConfPw(e.target.value)}
                autoComplete="new-password" required
                style={{ ...inp, border: `1.5px solid ${confPw && confPw !== newPw ? '#e74c3c' : confPw && confPw === newPw ? '#2ecc71' : 'rgba(201,168,76,0.3)'}` }}
              />
            </div>
            {error && <div style={{ background: 'rgba(192,57,43,0.2)', border: '1px solid rgba(192,57,43,0.4)', borderRadius: '6px', padding: '0.6rem 0.85rem', color: '#e74c3c', fontSize: '0.82rem' }}>{error}</div>}
            <button type="submit" disabled={loading || !newPw || !confPw} style={{ ...goldBtn, opacity: loading || !newPw || !confPw ? 0.55 : 1 }}>
              {loading ? 'Saving…' : t.setNewPassword}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
